import React from 'react';
import { ArrowRight, MapPin, Mail } from 'lucide-react';
import { businessInfo } from '../data/mockData';

export const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={businessInfo.truckImage}
          alt="Desi Fusion Food Truck"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-burgundy-950/95 via-red-900/90 to-orange-900/80"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-burgundy-950/60"></div>
      </div>

      {/* Decorative elements - More vibrant */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-600/30 to-yellow-600/30 backdrop-blur-sm border border-orange-400/40 rounded-full px-5 py-2.5 mb-6 shadow-lg shadow-orange-500/20">
            <MapPin className="text-orange-300" size={18} />
            <span className="text-orange-100 font-semibold text-sm">Mobile Food Truck • Events & Catering</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            {businessInfo.slogan.split(',').map((part, index) => (
              <span key={index} className="block">
                {part.trim()}
                {index === 2 && <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-500 animate-pulse">!</span>}
              </span>
            ))}
          </h1>

          <p className="text-xl md:text-2xl text-gray-100 mb-8 leading-relaxed">
            Authentic <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400 font-bold">Indian Street Food</span> delivered fresh from our vibrant mobile kitchen. We bring the flavors of India to your events, celebrations, and gatherings.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <button
              onClick={() => scrollToSection('menu')}
              className="group bg-gradient-to-r from-orange-600 via-orange-500 to-yellow-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-orange-700 hover:via-orange-600 hover:to-yellow-600 transition-all duration-300 hover:scale-105 shadow-2xl shadow-orange-500/50 hover:shadow-orange-600/60 flex items-center justify-center"
            >
              Explore Menu
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-white/10 backdrop-blur-md text-white border-2 border-white/40 px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/20 hover:border-emerald-400/60 transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-emerald-500/30"
            >
              <Mail className="mr-2" size={20} />
              Book Event
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-2xl">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent mb-1">100%</div>
              <div className="text-sm text-orange-200">Authentic</div>
            </div>
            <div className="text-center border-l border-r border-white/20">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent mb-1">Fresh</div>
              <div className="text-sm text-emerald-200">Daily</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent mb-1">Mobile</div>
              <div className="text-sm text-yellow-200">Service</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="animate-bounce">
          <div className="w-6 h-10 border-2 border-orange-400/70 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-orange-400/90 rounded-full"></div>
          </div>
        </div>
      </div>
    </section>
  );
};
